<script setup>
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BuhotextCampaignDialog from './BuhotextCampaignDialog.vue';
import BuhotextCampaignTableRow from './BuhotextCampaignTableRow.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useToggle } from '@vueuse/core';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const [showCampaignDialog, toggleCampaignDialog] = useToggle();
const showDeleteConfirmationPopup = ref(false);
const selectedCampaign = ref({});
const editingCampaign = ref(null);

const records = computed(() => getters['buhotextCampaigns/getCampaigns'].value);
const uiFlags = computed(() => getters['buhotextCampaigns/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedCampaign.value.name}?`);

onMounted(() => {
  store.dispatch('buhotextCampaigns/get');
});

const deleteCampaign = async id => {
  try {
    await store.dispatch('buhotextCampaigns/delete', id);
    useAlert(t('BUHOTEXT_CAMPAIGNS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('BUHOTEXT_CAMPAIGNS.DELETE.API.ERROR_MESSAGE'));
  }
};

const openDeletePopup = campaign => {
  showDeleteConfirmationPopup.value = true;
  selectedCampaign.value = campaign;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = () => {
  closeDeletePopup();
  deleteCampaign(selectedCampaign.value.id);
};

const openCreateDialog = () => {
  editingCampaign.value = null;
  toggleCampaignDialog(true);
};

const openEditDialog = campaign => {
  editingCampaign.value = campaign;
  toggleCampaignDialog(true);
};

const handleSendCampaign = async campaign => {
  try {
    await store.dispatch('buhotextCampaigns/send', campaign.id);
    useAlert(t('BUHOTEXT_CAMPAIGNS.SEND.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('BUHOTEXT_CAMPAIGNS.SEND.API.ERROR_MESSAGE'));
  }
};

const tableHeaders = computed(() => {
  return [
    t('BUHOTEXT_CAMPAIGNS.LIST.TABLE_HEADER.NAME'),
    t('BUHOTEXT_CAMPAIGNS.LIST.TABLE_HEADER.DEVICE'),
    t('BUHOTEXT_CAMPAIGNS.LIST.TABLE_HEADER.STATUS'),
    t('BUHOTEXT_CAMPAIGNS.LIST.TABLE_HEADER.SCHEDULED_AT'),
    t('BUHOTEXT_CAMPAIGNS.LIST.TABLE_HEADER.ACTIONS'),
  ];
});
</script>

<template>
  <SettingsLayout
    :no-records-message="$t('BUHOTEXT_CAMPAIGNS.LIST.404')"
    :no-records-found="!records.length"
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('BUHOTEXT_CAMPAIGNS.LOADING')"
    feature-name="buhotext_campaigns"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('BUHOTEXT_CAMPAIGNS.HEADER')"
        :description="$t('BUHOTEXT_CAMPAIGNS.DESCRIPTION')"
        feature-name="buhotext_campaigns"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('BUHOTEXT_CAMPAIGNS.HEADER_BTN_TXT')"
            @click="openCreateDialog"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <BuhotextCampaignTableRow
            v-for="(campaign, index) in records"
            :key="index"
            :campaign="campaign"
            @edit="openEditDialog"
            @delete="openDeletePopup"
            @send="handleSendCampaign"
          />
        </tbody>
      </table>
    </template>
  </SettingsLayout>

  <BuhotextCampaignDialog
    v-if="showCampaignDialog"
    :campaign="editingCampaign"
    @close="toggleCampaignDialog(false)"
  />

  <woot-delete-modal
    v-if="showDeleteConfirmationPopup"
    v-model:show="showDeleteConfirmationPopup"
    :on-close="closeDeletePopup"
    :on-confirm="confirmDeletion"
    :title="$t('BUHOTEXT_CAMPAIGNS.DELETE.CONFIRM.TITLE')"
    :message="$t('BUHOTEXT_CAMPAIGNS.DELETE.CONFIRM.MESSAGE')"
    :message-value="deleteMessage"
    :confirm-text="$t('BUHOTEXT_CAMPAIGNS.DELETE.CONFIRM.YES')"
    :reject-text="$t('BUHOTEXT_CAMPAIGNS.DELETE.CONFIRM.NO')"
  />
</template>
